import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { listarEmpreendimentos } from '../services/api';
import { api } from '../services/api';
import './ArquitetoDashboard.css';

interface ProjetoResumo {
  id: string;
  nome: string;
  titular: { id: string; nome: string };
  progressoGeral: number;
  dataInicio: string;
  dataPrevistaConclusao: string;
}

interface ObraAlerta {
  id: string;
  nome: string;
  titularNome: string;
  dataPrevistaConclusao: string;
  etapasIncompletas: number;
}

interface Resumo {
  totalObras: number;
  obrasAVencer: number;
  obrasVencidas: number;
}

type CardFilter = 'total' | 'em-andamento' | 'concluidos' | 'atrasados' | null;
type AbaAtiva = 'obras' | 'projetos';

interface TabState {
  searchTerm: string;
  activeCard: CardFilter;
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr);
  return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function getDaysRemaining(dateStr: string): number {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  return Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function getProjectStatus(progresso: number, dataPrevista: string): string {
  if (progresso === 100) return 'concluido';
  const days = getDaysRemaining(dataPrevista);
  if (days < 0) return 'atrasado';
  if (progresso > 0) return 'em-andamento';
  return 'nao-iniciado';
}

function getStatusLabel(status: string): string {
  switch (status) {
    case 'em-andamento': return 'Em andamento';
    case 'concluido': return 'Concluído';
    case 'atrasado': return 'Atrasado';
    case 'nao-iniciado': return 'Não iniciado';
    default: return '';
  }
}

export default function ArquitetoDashboard() {
  const navigate = useNavigate();
  const [abaAtiva, setAbaAtiva] = useState<AbaAtiva>('projetos');
  const [tabStates, setTabStates] = useState<Record<AbaAtiva, TabState>>({
    obras: { searchTerm: '', activeCard: null },
    projetos: { searchTerm: '', activeCard: null },
  });
  const [projetos, setProjetos] = useState<ProjetoResumo[]>([]);
  const [resumo, setResumo] = useState<Resumo>({ totalObras: 0, obrasAVencer: 0, obrasVencidas: 0 });
  const [obrasAVencer, setObrasAVencer] = useState<ObraAlerta[]>([]);
  const [obrasVencidas, setObrasVencidas] = useState<ObraAlerta[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [alertsOpen, setAlertsOpen] = useState(true);

  // Derive current tab state
  const currentTabState = tabStates[abaAtiva];
  const { searchTerm, activeCard } = currentTabState;

  // Update tab-specific state helpers
  function setSearchTerm(value: string) {
    setTabStates((prev) => ({
      ...prev,
      [abaAtiva]: { ...prev[abaAtiva], searchTerm: value },
    }));
  }

  function setActiveCard(value: CardFilter) {
    setTabStates((prev) => ({
      ...prev,
      [abaAtiva]: { ...prev[abaAtiva], activeCard: value },
    }));
  }

  // Fetch empreendimentos filtered by active tab type
  useEffect(() => {
    async function fetchData() {
      try {
        setLoading(true);
        const tipo = abaAtiva === 'obras' ? 'obra' : 'projeto';
        const [projetosData, resumoData, aVencerData, vencidasData] = await Promise.all([
          listarEmpreendimentos(tipo),
          api.get('/painel/resumo'),
          api.get('/painel/obras-a-vencer'),
          api.get('/painel/obras-vencidas'),
        ]);
        setProjetos(projetosData);
        setResumo(resumoData);
        setObrasAVencer(aVencerData);
        setObrasVencidas(vencidasData);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro ao carregar painel');
      } finally {
        setLoading(false);
      }
    }
    fetchData();
  }, [abaAtiva]);

  const derivedCounts = useMemo(() => {
    const emAndamento = projetos.filter(
      (p) => p.progressoGeral > 0 && p.progressoGeral < 100 && getDaysRemaining(p.dataPrevistaConclusao) >= 0
    ).length;
    const concluidos = projetos.filter((p) => p.progressoGeral === 100).length;
    const atrasados = projetos.filter(
      (p) => p.progressoGeral < 100 && getDaysRemaining(p.dataPrevistaConclusao) < 0
    ).length;
    return { emAndamento, concluidos, atrasados };
  }, [projetos]);

  const sortedByProgress = useMemo(() => {
    return [...projetos]
      .filter((p) => p.progressoGeral > 0)
      .sort((a, b) => b.progressoGeral - a.progressoGeral)
      .slice(0, 10);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  }, [projetos]);

  const filteredProjetos = useMemo(() => {
    let filtered = [...projetos];

    // Apply card filter
    if (activeCard === 'em-andamento') {
      filtered = filtered.filter(
        (p) => p.progressoGeral > 0 && p.progressoGeral < 100 && getDaysRemaining(p.dataPrevistaConclusao) >= 0
      );
    } else if (activeCard === 'concluidos') {
      filtered = filtered.filter((p) => p.progressoGeral === 100);
    } else if (activeCard === 'atrasados') {
      filtered = filtered.filter(
        (p) => p.progressoGeral < 100 && getDaysRemaining(p.dataPrevistaConclusao) < 0
      );
    }

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (p) =>
          p.nome.toLowerCase().includes(term) ||
          p.titular.nome.toLowerCase().includes(term)
      );
    }

    return filtered;
  }, [projetos, activeCard, searchTerm]);

  function handleCardClick(card: CardFilter) {
    setActiveCard(activeCard === card ? null : card);
  }

  // Contextual labels based on active tab
  const novoBtnLabel = abaAtiva === 'projetos' ? '➕ Novo Projeto' : '➕ Nova Obra';
  const novoBtnUrl = abaAtiva === 'projetos'
    ? '/painel/empreendimentos/novo?tipo=projeto'
    : '/painel/empreendimentos/novo?tipo=obra';
  const emptyMessage = abaAtiva === 'projetos' ? 'Nenhum projeto cadastrado.' : 'Nenhuma obra cadastrada.';
  const totalLabel = abaAtiva === 'projetos' ? 'Total Projetos' : 'Total Obras';
  const searchPlaceholder = abaAtiva === 'projetos'
    ? 'Buscar por nome ou titular...'
    : 'Buscar por nome ou titular...';
  const searchAriaLabel = abaAtiva === 'projetos' ? 'Buscar projetos' : 'Buscar obras';
  const tableSectionTitle = abaAtiva === 'projetos' ? 'Projetos' : 'Obras';

  if (loading) {
    return (
      <div className="arq-dashboard">
        <p className="arq-dashboard__loading">Carregando painel...</p>
      </div>
    );
  }

  if (error && projetos.length === 0) {
    return (
      <div className="arq-dashboard">
        <p className="arq-dashboard__error">{error}</p>
      </div>
    );
  }

  return (
    <div className="arq-dashboard">
      {/* Header */}
      <header className="arq-dashboard__header">
        <div>
          <h1 className="arq-dashboard__title">Painel Gerencial</h1>
          <p className="arq-dashboard__subtitle">Visão completa de todos os projetos e obras</p>
        </div>
      </header>

      {/* Tab Bar */}
      <div className="arq-dashboard__tabs" role="tablist">
        <button
          role="tab"
          className={`arq-dashboard__tab ${abaAtiva === 'projetos' ? 'arq-dashboard__tab--active' : ''}`}
          aria-selected={abaAtiva === 'projetos'}
          onClick={() => setAbaAtiva('projetos')}
        >
          Projetos
        </button>
        <button
          role="tab"
          className={`arq-dashboard__tab ${abaAtiva === 'obras' ? 'arq-dashboard__tab--active' : ''}`}
          aria-selected={abaAtiva === 'obras'}
          onClick={() => setAbaAtiva('obras')}
        >
          Obras
        </button>
        <button
          className="arq-dashboard__new-project-btn"
          onClick={() => navigate(novoBtnUrl)}
        >
          {novoBtnLabel}
        </button>
      </div>

      {/* Summary Cards */}
      <section className="arq-dashboard__cards" aria-label="Indicadores resumo">
        <button
          className={`arq-card${activeCard === 'total' ? ' arq-card--active' : ''}`}
          onClick={() => handleCardClick('total')}
          aria-pressed={activeCard === 'total'}
        >
          <span className="arq-card__icon" aria-hidden="true">🏗️</span>
          <span className="arq-card__number">{projetos.length}</span>
          <span className="arq-card__label">{totalLabel}</span>
        </button>

        <button
          className={`arq-card${activeCard === 'em-andamento' ? ' arq-card--active' : ''}`}
          onClick={() => handleCardClick('em-andamento')}
          aria-pressed={activeCard === 'em-andamento'}
        >
          <span className="arq-card__icon" aria-hidden="true">⚡</span>
          <span className="arq-card__number">{derivedCounts.emAndamento}</span>
          <span className="arq-card__label">Em Andamento</span>
        </button>

        <button
          className={`arq-card${activeCard === 'concluidos' ? ' arq-card--active' : ''}`}
          onClick={() => handleCardClick('concluidos')}
          aria-pressed={activeCard === 'concluidos'}
        >
          <span className="arq-card__icon" aria-hidden="true">✅</span>
          <span className="arq-card__number">{derivedCounts.concluidos}</span>
          <span className="arq-card__label">Concluídos</span>
        </button>

        <button
          className={`arq-card arq-card--danger${activeCard === 'atrasados' ? ' arq-card--active' : ''}`}
          onClick={() => handleCardClick('atrasados')}
          aria-pressed={activeCard === 'atrasados'}
        >
          <span className="arq-card__icon" aria-hidden="true">⚠️</span>
          <span className="arq-card__number">{derivedCounts.atrasados}</span>
          <span className="arq-card__label">Atrasados</span>
        </button>
      </section>

      {/* Alerts Section */}
      {(obrasAVencer.length > 0 || obrasVencidas.length > 0) && (
        <section className="arq-dashboard__alerts-section">
          <button
            className="arq-dashboard__alerts-toggle"
            onClick={() => setAlertsOpen(!alertsOpen)}
            aria-expanded={alertsOpen}
          >
            <h2 className="arq-dashboard__section-title" style={{ margin: 0 }}>
              Alertas de Prazo
            </h2>
            <span className="arq-dashboard__alerts-chevron" aria-hidden="true">
              {alertsOpen ? '▼' : '▶'}
            </span>
          </button>

          {alertsOpen && (
            <div className="arq-alerts__content">
              {obrasAVencer.length > 0 && (
                <div className="arq-alerts__group">
                  <h3 className="arq-alerts__group-title arq-alerts__group-title--warning">
                    🟡 Obras a Vencer ({obrasAVencer.length})
                  </h3>
                  <div className="arq-alerts__list">
                    {obrasAVencer.map((obra) => {
                      const days = getDaysRemaining(obra.dataPrevistaConclusao);
                      return (
                        <div
                          key={obra.id}
                          className="arq-alerts__item arq-alerts__item--warning"
                          onClick={() => navigate(`/empreendimentos/${obra.id}`)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(`/empreendimentos/${obra.id}`);
                            }
                          }}
                        >
                          <div className="arq-alerts__item-info">
                            <span className="arq-alerts__item-name">{obra.nome}</span>
                            <span className="arq-alerts__item-titular">{obra.titularNome}</span>
                          </div>
                          <div className="arq-alerts__item-meta">
                            <span className="arq-alerts__item-date">{formatDate(obra.dataPrevistaConclusao)}</span>
                            <span className="arq-alerts__item-days arq-alerts__item-days--warning">
                              {days === 0 ? 'Vence hoje' : `${days} dia${days !== 1 ? 's' : ''}`}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {obrasVencidas.length > 0 && (
                <div className="arq-alerts__group">
                  <h3 className="arq-alerts__group-title arq-alerts__group-title--danger">
                    🔴 Obras Vencidas ({obrasVencidas.length})
                  </h3>
                  <div className="arq-alerts__list">
                    {obrasVencidas.map((obra) => {
                      const days = Math.abs(getDaysRemaining(obra.dataPrevistaConclusao));
                      return (
                        <div
                          key={obra.id}
                          className="arq-alerts__item arq-alerts__item--danger"
                          onClick={() => navigate(`/empreendimentos/${obra.id}`)}
                          role="button"
                          tabIndex={0}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              navigate(`/empreendimentos/${obra.id}`);
                            }
                          }}
                        >
                          <div className="arq-alerts__item-info">
                            <span className="arq-alerts__item-name">{obra.nome}</span>
                            <span className="arq-alerts__item-titular">{obra.titularNome}</span>
                          </div>
                          <div className="arq-alerts__item-meta">
                            <span className="arq-alerts__item-date">{formatDate(obra.dataPrevistaConclusao)}</span>
                            <span className="arq-alerts__item-days arq-alerts__item-days--danger">
                              {days} dia{days !== 1 ? 's' : ''} atrás
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </section>
      )}

      {/* Projects/Obras Table */}
      <section className="arq-dashboard__table-section">
        <div className="arq-dashboard__table-header">
          <h2 className="arq-dashboard__section-title">{tableSectionTitle}</h2>
          <div className="arq-dashboard__table-actions">
            <input
              type="text"
              className="arq-dashboard__search-input"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              aria-label={searchAriaLabel}
            />
          </div>
        </div>

        <div className="arq-dashboard__table-container">
          {filteredProjetos.length > 0 ? (
            <table className="arq-table">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Titular</th>
                  <th>Progresso</th>
                  <th>Início</th>
                  <th>Previsão</th>
                  <th>Status</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredProjetos.map((projeto) => {
                  const status = getProjectStatus(projeto.progressoGeral, projeto.dataPrevistaConclusao);
                  return (
                    <tr
                      key={projeto.id}
                      className="arq-table__row"
                      onClick={() => navigate(`/empreendimentos/${projeto.id}`)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          navigate(`/empreendimentos/${projeto.id}`);
                        }
                      }}
                      tabIndex={0}
                      role="link"
                      aria-label={`Ver ${abaAtiva === 'projetos' ? 'projeto' : 'obra'} ${projeto.nome}`}
                    >
                      <td className="arq-table__cell-name">{projeto.nome}</td>
                      <td>{projeto.titular.nome}</td>
                      <td>
                        <div className="arq-table__progress">
                          <div className="arq-table__progress-track">
                            <div
                              className="arq-table__progress-fill"
                              style={{ width: `${projeto.progressoGeral}%` }}
                            />
                          </div>
                          <span className="arq-table__progress-value">{projeto.progressoGeral}%</span>
                        </div>
                      </td>
                      <td>{formatDate(projeto.dataInicio)}</td>
                      <td>{formatDate(projeto.dataPrevistaConclusao)}</td>
                      <td>
                        <span className={`arq-table__status arq-table__status--${status}`}>
                          {getStatusLabel(status)}
                        </span>
                      </td>
                      <td className="arq-table__actions-cell">
                        <button
                          className="arq-table__action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/painel/empreendimentos/${projeto.id}/editar`);
                          }}
                          aria-label={`Editar ${projeto.nome}`}
                          title="Editar"
                        >
                          ✏️
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <p className="arq-dashboard__empty">
              {searchTerm || activeCard
                ? `Nenhum ${abaAtiva === 'projetos' ? 'projeto' : 'obra'} encontrado com os filtros aplicados.`
                : emptyMessage}
            </p>
          )}
        </div>
      </section>

      {/* Quick Actions */}
      <section className="arq-dashboard__quick-actions">
        <button
          className="arq-quick-action"
          onClick={() => navigate('/painel/usuarios')}
        >
          👥 Gerenciar Usuários
        </button>
        <button
          className="arq-quick-action"
          onClick={() => navigate('/painel/tipos-etapa')}
        >
          📋 Tipos de Etapa
        </button>
      </section>
    </div>
  );
}
